import React, { useState, useRef, useEffect } from 'react';
import { EventParameter } from '../../../generated/api/models/EventParameter';
import { getDefaultValue } from './typeUtils';
import Icon from '../NewIcon';

interface ParameterChipsProps {
  parameters: EventParameter[];
  onInsertParameter: (paramName: string) => void;
  currentValues?: { [paramName: string]: any };
  onValueChange?: (paramName: string, value: any) => void;
  onResetValue?: (paramName: string) => void;
}

const ParameterChips: React.FC<ParameterChipsProps> = ({
  parameters,
  onInsertParameter,
  currentValues: externalCurrentValues,
  onValueChange: externalOnValueChange,
  onResetValue: externalOnResetValue,
}) => {
  // Internal state for uncontrolled mode (when parent doesn't provide state management)
  const [internalCurrentValues, setInternalCurrentValues] = useState<{ [paramName: string]: any }>(
    {},
  );

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = externalCurrentValues !== undefined;
  const currentValues = isControlled ? externalCurrentValues : internalCurrentValues;

  // Use external handlers if provided, otherwise use internal state management
  const onValueChange =
    externalOnValueChange ||
    ((paramName: string, value: any) => {
      setInternalCurrentValues((prev) => ({
        ...prev,
        [paramName]: value,
      }));
    });

  const onResetValue =
    externalOnResetValue ||
    ((paramName: string) => {
      setInternalCurrentValues((prev) => {
        const newValues = { ...prev };
        delete newValues[paramName];
        return newValues;
      });
    });

  // State for tracking which parameter is being edited (for text/number types)
  const [editingParam, setEditingParam] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // State for tracking which parameter's enum menu is open
  const [enumMenuParam, setEnumMenuParam] = useState<string | null>(null);
  const enumMenuRef = useRef<HTMLDivElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingParam && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingParam]);

  // Close enum menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (enumMenuRef.current && !enumMenuRef.current.contains(event.target as Node)) {
        setEnumMenuParam(null);
      }
    };

    if (enumMenuParam) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [enumMenuParam]);

  // Convert camelCase to Title Case (e.g., "otherPatient" -> "Other Patient")
  const toTitleCase = (str: string): string => {
    return str
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Get current value for a parameter (custom or default)
  const getCurrentValue = (param: EventParameter): any => {
    if (currentValues && param.name in currentValues) {
      return currentValues[param.name];
    }
    return getDefaultValue(param.example, param.type);
  };

  // Check if a parameter has been customized (value differs from default)
  const isCustomized = (param: EventParameter): boolean => {
    if (!currentValues || !(param.name in currentValues)) {
      return false;
    }

    const currentValue = currentValues[param.name];
    const defaultValue = getDefaultValue(param.example, param.type);

    // Deep comparison for different types
    return currentValue !== defaultValue;
  };

  // Handle starting edit mode for text/number parameters
  const startEdit = (param: EventParameter) => {
    const currentValue = getCurrentValue(param);
    setEditValue(String(currentValue));
    setEditingParam(param.name);
  };

  // Handle saving edited value
  const saveEdit = (param: EventParameter) => {
    if (!onValueChange) return;

    // Convert the string value to the appropriate type
    let finalValue: any = editValue;
    if (
      param.type.toLowerCase() === 'number' ||
      param.type.toLowerCase() === 'integer' ||
      param.type.toLowerCase() === 'float'
    ) {
      const num = Number(editValue);
      if (!isNaN(num)) {
        finalValue = num;
      }
    }

    // Check if the new value equals the default
    const defaultValue = getDefaultValue(param.example, param.type);
    if (finalValue === defaultValue && onResetValue) {
      // If it equals default, remove it from customValues instead of setting it
      onResetValue(param.name);
    } else {
      onValueChange(param.name, finalValue);
    }

    setEditingParam(null);
    setEditValue('');
  };

  // Handle canceling edit mode
  const cancelEdit = () => {
    setEditingParam(null);
    setEditValue('');
  };

  // Handle toggling boolean value
  const toggleBoolean = (param: EventParameter) => {
    if (!onValueChange) return;
    const currentValue = getCurrentValue(param);
    const newValue = !currentValue;

    // Check if the new value equals the default
    const defaultValue = getDefaultValue(param.example, param.type);
    if (newValue === defaultValue && onResetValue) {
      // If it equals default, remove it from customValues instead of setting it
      onResetValue(param.name);
    } else {
      onValueChange(param.name, newValue);
    }
  };

  // Handle selecting enum value
  const selectEnumValue = (param: EventParameter, value: string) => {
    if (!onValueChange) return;

    // Check if the new value equals the default
    const defaultValue = getDefaultValue(param.example, param.type);
    if (value === defaultValue && onResetValue) {
      // If it equals default, remove it from customValues instead of setting it
      onResetValue(param.name);
    } else {
      onValueChange(param.name, value);
    }

    setEnumMenuParam(null);
  };

  // Handle resetting to default value
  const resetValue = (paramName: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering the chip click
    if (!onResetValue) return;

    // Remove from customValues
    onResetValue(paramName);
  };

  // Convert parameters to organized structure by category and entity
  // Entity is the first part before the dot in param.name (e.g., "patient" from "patient.id")
  const parametersByCategoryAndEntity = parameters.reduce(
    (acc, param) => {
      const category = param.category || 'General';

      // Extract entity from param.name (e.g., "patient" from "patient.id")
      const entity = param.name?.split('.')?.[0] || 'general';

      if (!acc[category]) {
        acc[category] = {};
      }
      if (!acc[category]![entity]) {
        acc[category]![entity] = [];
      }
      acc[category]![entity]!.push(param);
      return acc;
    },
    {} as { [category: string]: { [entity: string]: EventParameter[] } },
  );

  if (parameters.length === 0) {
    return null;
  }

  // Render a single chip based on its type and state
  const renderChip = (param: EventParameter) => {
    const currentValue = getCurrentValue(param);
    const customized = isCustomized(param);
    const isEditing = editingParam === param.name;
    const isEnumOpen = enumMenuParam === param.name;

    // Interactive chips with different behavior per type
    const chipClassName = `parameter-chip interactive ${customized ? 'customized' : ''}`;

    // BOOLEAN TYPE: Click to toggle, show check/X icon
    if (param.type.toLowerCase() === 'boolean') {
      return (
        <div key={param.name} className="chip-wrapper">
          <button
            className={chipClassName}
            onClick={() => toggleBoolean(param)}
            title={`${param.description} (click to toggle)`}
          >
            <Icon
              src={currentValue ? 'check' : 'times'}
              height="12px"
              style={{ color: currentValue ? '#10b981' : '#ef4444' }}
            />
            <span className="chip-label">{param.description}</span>
          </button>
          <button
            className="chip-insert"
            onClick={(e) => {
              e.stopPropagation();
              onInsertParameter(param.name);
            }}
            title="Insert parameter"
          >
            <Icon src="plus" height="10px" />
          </button>
          {customized && (
            <button
              className="chip-reset"
              onClick={(e) => resetValue(param.name, e)}
              title="Reset to default"
            >
              <Icon src="rotate-left" height="10px" />
            </button>
          )}
        </div>
      );
    }

    // ENUM TYPE: Click to show dropdown menu
    if (param.enum_values && param.enum_values.length > 0) {
      return (
        <div key={param.name} className="chip-wrapper" ref={isEnumOpen ? enumMenuRef : undefined}>
          <button
            className={chipClassName}
            onClick={() => setEnumMenuParam(isEnumOpen ? null : param.name)}
            title={`${param.description} (click to change)`}
          >
            <span className="chip-label">{param.description}:</span>
            <span className="chip-value">{String(currentValue)}</span>
            <Icon src="chevron-down" height="10px" />
          </button>
          <button
            className="chip-insert"
            onClick={(e) => {
              e.stopPropagation();
              onInsertParameter(param.name);
            }}
            title="Insert parameter"
          >
            <Icon src="plus" height="10px" />
          </button>
          {customized && (
            <button
              className="chip-reset"
              onClick={(e) => resetValue(param.name, e)}
              title="Reset to default"
            >
              <Icon src="rotate-left" height="10px" />
            </button>
          )}
          {isEnumOpen && (
            <div className="enum-menu">
              {param.enum_values.map((enumValue) => (
                <button
                  key={enumValue}
                  className={`enum-option ${currentValue === enumValue ? 'selected' : ''}`}
                  onClick={() => selectEnumValue(param, enumValue)}
                >
                  {enumValue}
                  {currentValue === enumValue && <Icon src="check" height="12px" />}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // STRING/NUMBER TYPE: Click to edit inline
    if (isEditing) {
      return (
        <div key={param.name} className="chip-wrapper editing">
          <input
            ref={editInputRef}
            type={
              param.type.toLowerCase() === 'number' ||
              param.type.toLowerCase() === 'integer' ||
              param.type.toLowerCase() === 'float'
                ? 'number'
                : 'text'
            }
            className="chip-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                saveEdit(param);
              } else if (e.key === 'Escape') {
                cancelEdit();
              }
            }}
            onBlur={() => saveEdit(param)}
            placeholder={param.description}
          />
        </div>
      );
    }

    return (
      <div key={param.name} className="chip-wrapper">
        <button
          className={chipClassName}
          onClick={() => startEdit(param)}
          title={`${param.description} (click to edit)`}
        >
          <span className="chip-label">{param.description}:</span>
          <span className="chip-value">{String(currentValue)}</span>
        </button>
        <button
          className="chip-insert"
          onClick={(e) => {
            e.stopPropagation();
            onInsertParameter(param.name);
          }}
          title="Insert parameter"
        >
          <Icon src="plus" height="10px" />
        </button>
        {customized && (
          <button
            className="chip-reset"
            onClick={(e) => resetValue(param.name, e)}
            title="Reset to default"
          >
            <Icon src="rotate-left" height="10px" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="parameter-chips">
      <div className="chips-label">Available Parameters:</div>
      <div className="chips-container">
        {Object.entries(parametersByCategoryAndEntity).map(([category, entitiesObj]) =>
          Object.entries(entitiesObj).map(([entity, params]) => (
            <div key={`${category}-${entity}`} className="chip-category">
              <span className="category-label">
                {category}
                {entity.toLowerCase !== category.toLowerCase ? ` (${entity})` : ''}:
              </span>
              {(params as EventParameter[]).map((param: EventParameter) => renderChip(param))}
            </div>
          )),
        )}
      </div>
    </div>
  );
};

export default ParameterChips;
